/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings
} from '@msdyn365-commerce/core';
import {
    ChannelReferenceLookupCriteria,
    OrderShipments,
    SalesOrder,
    SalesOrdersDataActions,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';

/**
 * IOrderHistory.
 */
export interface IOrderHistory {
    salesOrders: OrderShipments[];
    products: SimpleProduct[];
}

/**
 * Action input.
 */
export class GetSalesOrderDetailsByConfirmationInput implements IActionInput {
    public channelReferenceLookupCriteria: ChannelReferenceLookupCriteria | null;

    public confirmationId: string | undefined;

    private readonly apiSettings: ICommerceApiSettings;

    public constructor(channelReferenceLookupCriteria: ChannelReferenceLookupCriteria | null, apiSettings: ICommerceApiSettings) {
        this.channelReferenceLookupCriteria = channelReferenceLookupCriteria;
        this.apiSettings = apiSettings;
        this.confirmationId = channelReferenceLookupCriteria?.ChannelReferenceId;
    }

    /**
     * GetCacheKey.
     * @returns - String.
     */
    public getCacheKey = (): string => buildCacheKey(`OrderDetails-${this.confirmationId ? this.confirmationId : ''}`, this.apiSettings);

    /**
     * GetCacheObjectType.
     * @returns - String.
     */
    public getCacheObjectType = (): string => 'OrderDetails';

    /**
     * DataCacheType.
     * @returns - String.
     */
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Get sales order details by confirmation id action.
 * @param input - GetOrderHistory hydration input.
 * @param context - Channel context.
 * @returns - Sales Order Details.
 */
export async function getSalesOrderDetailsByChannelReferenceIdAction(
    input: GetSalesOrderDetailsByConfirmationInput,
    context: IActionContext
): Promise<SalesOrder> {
    return SalesOrdersDataActions.getOrderByChannelReferenceLookupCriteriaAsync(
        { callerContext: context },
        input.channelReferenceLookupCriteria
    );
}

/**
 * GetSalesOrderDetailsByConfirmationIdDataAction.
 * @returns -- IObservableAction<SalesOrder>.
 */
export const getSalesOrderDetailsByChannelReferenceIdDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/order-lookup/get-sales-order-details-by-confirmation-id',
    action: getSalesOrderDetailsByChannelReferenceIdAction as IAction<SalesOrder>
});

export default getSalesOrderDetailsByChannelReferenceIdDataAction;
